import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * QuickEditDialog - Restricted field edit modal for Contacts table Actions
 * Only allows editing: Shortcode, Company Name, Contact Name, Type, Phone, Email
 */
const QuickEditDialog = ({ 
  open, 
  onOpenChange, 
  contact, 
  getAuthHeaders, 
  onSuccess 
}) => {
  const [formData, setFormData] = useState({
    shortcode: '',
    company_name: '',
    name: '',
    contactType: 'merchant',
    phone: '',
    email: ''
  });
  const [saving, setSaving] = useState(false);

  // Initialize form when contact changes
  useEffect(() => {
    if (contact) {
      // Determine primary contact type
      let contactType = 'other';
      if (contact.is_merchant) contactType = 'merchant';
      else if (contact.is_broker) contactType = 'broker';
      else if (contact.is_investor) contactType = 'investor';
      else if (contact.is_funder) contactType = 'funder';
      else if (contact.is_iso) contactType = 'iso';

      setFormData({
        shortcode: contact.shortcode || '',
        company_name: contact.company_name || '',
        name: contact.name || '',
        contactType,
        phone: contact.phone || contact.phones?.[0]?.phone || '',
        email: contact.email || contact.emails?.[0]?.email || ''
      });
    }
  }, [contact]);

  const handleSave = async () => {
    if (!contact) return;
    
    setSaving(true);
    try {
      // Build update payload - only the allowed fields
      const updateData = {
        ...contact, // Keep existing data
        shortcode: formData.shortcode,
        company_name: formData.company_name,
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        // Update phones/emails arrays for consistency
        phones: formData.phone ? [{ phone: formData.phone, type: 'Main' }] : contact.phones || [],
        emails: formData.email ? [{ email: formData.email, type: 'Business' }] : contact.emails || [],
        // Reset type flags and set the selected one
        is_merchant: formData.contactType === 'merchant',
        is_broker: formData.contactType === 'broker',
        is_investor: formData.contactType === 'investor',
        is_funder: formData.contactType === 'funder',
        is_iso: formData.contactType === 'iso',
        is_other: formData.contactType === 'other'
      };

      await axios.put(`${API}/contacts/${contact.id}`, updateData, {
        headers: getAuthHeaders()
      });

      toast.success('Contact updated');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Quick edit error:', error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="quick-edit-dialog">
        <DialogHeader>
          <DialogTitle>Quick Edit Contact</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="shortcode">Shortcode</Label>
              <Input
                id="shortcode"
                data-testid="quick-edit-shortcode"
                value={formData.shortcode}
                onChange={(e) => setFormData(prev => ({ ...prev, shortcode: e.target.value }))}
                placeholder="e.g., ABC123"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contactType">Type</Label>
              <Select
                value={formData.contactType}
                onValueChange={(value) => setFormData(prev => ({ ...prev, contactType: value }))}
              >
                <SelectTrigger id="contactType" data-testid="quick-edit-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="merchant">Merchant</SelectItem>
                  <SelectItem value="broker">Broker</SelectItem>
                  <SelectItem value="investor">Investor/Syndicator</SelectItem>
                  <SelectItem value="funder">Funder</SelectItem>
                  <SelectItem value="iso">ISO</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="company_name">Business Name (Company)</Label>
            <Input
              id="company_name"
              data-testid="quick-edit-company"
              value={formData.company_name}
              onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
              placeholder="Company name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Contact Name (Person)</Label>
            <Input
              id="name"
              data-testid="quick-edit-name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Contact person name"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                data-testid="quick-edit-phone"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="Phone number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="quick-edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Email address"
              />
            </div>
          </div>

          <p className="text-xs text-gray-500 mt-2">
            For full editing (address, extended info), open the Contact Profile.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="quick-edit-save">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickEditDialog;
